// ============================================================================
// Advisory recurrence schedule — pure, dependency-free time-zone helpers.
// ----------------------------------------------------------------------------
// Recurring advisories are listed for their entire OUTER date range (spec §1.1)
// so officers can review them at shift start even outside the daily slot. These
// helpers let the board card say, at a glance, whether a recurring advisory is
// in effect RIGHT NOW and — when it isn't — when its window next opens.
//
// All zone math uses Intl.DateTimeFormat with an explicit timeZone (no deps).
// The daily slot times (recurrenceStartTime/EndTime) are wall-clock times in
// the advisory's recurrenceZoneId; "now" and the returned Dates are absolute
// instants, so the card can format them in the VIEWER's local zone.
// ============================================================================

import {
  RECURRENCE_DAY_CODES,
  RECURRENCE_DAY_LABELS,
  hhmmToMinutes,
  browserZoneId,
} from './advisoryValidation';

/** The recurrence fields a board row carries (subset of AdvisoryListItem). */
export interface RecurrenceScheduleInput {
  recurrenceDays: string[];
  recurrenceStartTime: string | null;
  recurrenceEndTime: string | null;
  recurrenceZoneId: string | null;
  /** Outer date range (ISO strings); the recurrence only applies within it. */
  startDateTime: string | null;
  endDateTime: string | null;
}

export interface RecurrenceState {
  /** Is "now" inside the daily slot, on an active weekday, within the outer range? */
  inSlotNow: boolean;
  /** When inSlotNow: the instant the current slot closes (else null). */
  currentSlotEnd: Date | null;
  /** The next instant the slot opens (strictly future), capped by the outer end; */
  /** null when no further window remains. */
  nextWindowStart: Date | null;
}

// 0=Sun..6=Sat (JS getUTCDay order) → our MON..SUN codes.
const DOW_CODES = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'] as const;
const DAY_MS = 24 * 60 * 60 * 1000;

interface ZoneParts {
  year: number;
  month: number; // 1-12
  day: number;
  hour: number; // 0-23
  minute: number;
  weekday: string; // "MON".."SUN"
}

/** The wall-clock components of an instant, as rendered in `timeZone`. */
function zonePartsOf(instant: Date, timeZone: string): ZoneParts {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    weekday: 'short',
  });
  const map: Record<string, string> = {};
  for (const part of dtf.formatToParts(instant)) {
    if (part.type !== 'literal') map[part.type] = part.value;
  }
  return {
    year: parseInt(map.year, 10),
    month: parseInt(map.month, 10),
    day: parseInt(map.day, 10),
    hour: parseInt(map.hour, 10),
    minute: parseInt(map.minute, 10),
    weekday: (map.weekday || '').toUpperCase(),
  };
}

/**
 * The UTC instant whose wall-clock representation in `timeZone` is the given
 * Y/M/D H:m. Uses the standard "format-and-correct-by-offset" technique so DST
 * offsets are honored without any dependency.
 */
function zonedWallTimeToUtc(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  timeZone: string
): Date {
  const asUtc = Date.UTC(year, month - 1, day, hour, minute, 0);
  const p = zonePartsOf(new Date(asUtc), timeZone);
  const zoneAsUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, 0);
  const offset = asUtc - zoneAsUtc;
  return new Date(asUtc + offset);
}

/**
 * Compute the live schedule state of a recurring advisory relative to `now`.
 * Returns all-false/null when the recurrence fields are incomplete.
 */
export function computeRecurrenceState(
  input: RecurrenceScheduleInput,
  now: Date
): RecurrenceState {
  const empty: RecurrenceState = {
    inSlotNow: false,
    currentSlotEnd: null,
    nextWindowStart: null,
  };

  const days = input.recurrenceDays || [];
  const startMin = hhmmToMinutes(input.recurrenceStartTime);
  const endMin = hhmmToMinutes(input.recurrenceEndTime);
  if (days.length === 0 || startMin == null || endMin == null) return empty;

  const zone = input.recurrenceZoneId || browserZoneId();
  const outerStart = input.startDateTime ? new Date(input.startDateTime) : null;
  const outerEnd = input.endDateTime ? new Date(input.endDateTime) : null;
  const nowMs = now.getTime();

  const withinOuter =
    (!outerStart || nowMs >= outerStart.getTime()) &&
    (!outerEnd || nowMs <= outerEnd.getTime());

  const startHour = Math.floor(startMin / 60);
  const startMinute = startMin % 60;
  const endHour = Math.floor(endMin / 60);
  const endMinute = endMin % 60;

  // --- In-slot check against "now" in the advisory's zone ---
  // Same-day slot (start < end): in slot on a selected day between the times.
  // Overnight slot (end < start): spans midnight into the following day —
  // selected days are the days the window STARTS, so "now" is in slot either
  // past the start on a selected day, or before the end on the day AFTER a
  // selected day. End stays exclusive in both shapes.
  const p = zonePartsOf(now, zone);
  const nowMin = p.hour * 60 + p.minute;
  const overnight = endMin < startMin;
  let inSlotNow = false;
  let currentSlotEnd: Date | null = null;
  if (withinOuter && !overnight) {
    if (days.includes(p.weekday) && nowMin >= startMin && nowMin < endMin) {
      inSlotNow = true;
      currentSlotEnd = zonedWallTimeToUtc(p.year, p.month, p.day, endHour, endMinute, zone);
    }
  } else if (withinOuter && overnight) {
    if (days.includes(p.weekday) && nowMin >= startMin) {
      // Entered via today's start: the slot closes at the end wall-time TOMORROW.
      inSlotNow = true;
      const t = zonePartsOf(new Date(now.getTime() + DAY_MS), zone);
      currentSlotEnd = zonedWallTimeToUtc(t.year, t.month, t.day, endHour, endMinute, zone);
    } else if (nowMin < endMin) {
      // Entered via YESTERDAY's start (wrapped past midnight): closes today.
      const y = zonePartsOf(new Date(now.getTime() - DAY_MS), zone);
      if (days.includes(y.weekday)) {
        inSlotNow = true;
        currentSlotEnd = zonedWallTimeToUtc(p.year, p.month, p.day, endHour, endMinute, zone);
      }
    }
  }

  // --- Next window search, day by day in the advisory's zone ---
  // Anchor at the later of "now" and the outer start (a SCHEDULED advisory's
  // first window can't precede its start). Weekly recurrence guarantees a match
  // within 7 days of the anchor, so a small cap is sufficient; the extra days
  // absorb an outer-end cutoff landing on non-selected weekdays.
  const anchor =
    outerStart && outerStart.getTime() > nowMs ? outerStart : now;
  const ap = zonePartsOf(anchor, zone);
  let cursor = Date.UTC(ap.year, ap.month - 1, ap.day);
  let nextWindowStart: Date | null = null;
  for (let i = 0; i < 15; i++) {
    const d = new Date(cursor);
    const y = d.getUTCFullYear();
    const mo = d.getUTCMonth() + 1;
    const day = d.getUTCDate();
    const dow = DOW_CODES[d.getUTCDay()];
    if (days.includes(dow)) {
      const openInstant = zonedWallTimeToUtc(y, mo, day, startHour, startMinute, zone);
      const t = openInstant.getTime();
      if (outerEnd && t > outerEnd.getTime()) break; // no further windows fit
      if (t > nowMs && (!outerStart || t >= outerStart.getTime())) {
        nextWindowStart = openInstant;
        break;
      }
    }
    cursor += DAY_MS;
  }

  return { inSlotNow, currentSlotEnd, nextWindowStart };
}

/**
 * Plain-language day summary: "Every day" / "Weekdays" / "Weekends" /
 * "Mon, Wed, Fri" (week order). Day abbreviations are always capitalized.
 * When `overnight` is true (window ends the following day), the labels take
 * their "nights" form: "Nightly" / "Weekday nights" / "Weekend nights" /
 * "Tue, Thu nights" — the days are still the days the window STARTS.
 */
export function formatRecurrenceDays(days: string[], overnight = false): string {
  const set = new Set(days);
  if (set.size === 0) return '';
  const has = (c: string) => set.has(c);
  const weekdays = ['MON', 'TUE', 'WED', 'THU', 'FRI'];
  const weekend = ['SAT', 'SUN'];
  if (set.size === 7) return overnight ? 'Nightly' : 'Every day';
  if (set.size === 5 && weekdays.every(has)) return overnight ? 'Weekday nights' : 'Weekdays';
  if (set.size === 2 && weekend.every(has)) return overnight ? 'Weekend nights' : 'Weekends';
  const list = RECURRENCE_DAY_CODES.filter(has)
    .map((c) => RECURRENCE_DAY_LABELS[c])
    .join(', ');
  return overnight ? `${list} nights` : list;
}

/** Short zone label (e.g. "PST", "EST") for the given zone at an instant. */
export function zoneShortName(zoneId: string, at: Date): string {
  try {
    const dtf = new Intl.DateTimeFormat('en-US', {
      timeZone: zoneId,
      timeZoneName: 'short',
    });
    const part = dtf.formatToParts(at).find((x) => x.type === 'timeZoneName');
    return part ? part.value : '';
  } catch {
    return '';
  }
}
